/**
 * `<EditorActionItems>` — the editable Action items list in the Editor's right pane.
 *
 * Action items are directly user-editable (migration 0017): the status checkbox toggles
 * completion, the text + due date are editable inline, each row can be deleted, and a
 * "+ Add action item" affordance creates a user-owned item. Every mutation writes the
 * `action_items` row — the single source of truth both MCP (`get_action_items` / `get_note`)
 * and the in-app chat read — and is stamped user-edited server-side so a later re-extraction
 * never reverts it.
 *
 * Status / text / due / delete are applied optimistically against the cached `NoteView`
 * (instant feedback, rolled back on failure); create refetches (no id until the server mints
 * one). All paths invalidate `['note', noteId]` to reconcile.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components';
import {
  setActionItemStatus,
  createActionItem,
  updateActionItem,
  deleteActionItem,
} from '@/api';
import { queryKeys } from '@/providers';
import type { ActionItemView, NoteId, NoteView } from '@/types';

interface EditorActionItemsProps {
  /** The current note id — `null` for an unsaved scratch buffer (renders the empty hint only). */
  noteId: NoteId | null;
  /** The note's action items, from `companion.actionItems`. */
  actionItems: ActionItemView[];
}

/** The date-input value (`YYYY-MM-DD`) for an ISO due-date string, or '' when there is none. */
function toDateInput(due: string | null): string {
  return due ? due.slice(0, 10) : '';
}

/** A compact human due-date label for read mode, or null when there is no due date. */
function dueLabel(due: string | null): string | null {
  if (!due) return null;
  try {
    return new Date(due).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return due.slice(0, 10);
  }
}

export function EditorActionItems({ noteId, actionItems }: EditorActionItemsProps): JSX.Element {
  const qc = useQueryClient();

  // Inline edit state: which row is in edit mode + its drafts. `adding` reveals the new-item row.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [draftDue, setDraftDue] = useState('');
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newDue, setNewDue] = useState('');

  const cacheKey = noteId === null ? null : [...queryKeys.note(noteId), true];

  /** Optimistically rewrite the cached action items; returns the prior `NoteView` for rollback. */
  const patchItems = (
    updater: (items: ActionItemView[]) => ActionItemView[],
  ): NoteView | undefined => {
    if (cacheKey === null) return undefined;
    const prev = qc.getQueryData<NoteView>(cacheKey);
    if (prev?.companion) {
      qc.setQueryData<NoteView>(cacheKey, {
        ...prev,
        companion: { ...prev.companion, actionItems: updater(prev.companion.actionItems) },
      });
    }
    return prev;
  };

  const invalidate = (): void => {
    if (noteId !== null) void qc.invalidateQueries({ queryKey: ['note', noteId] });
  };

  /** Run an optimistic mutation: apply `updater` to the cache, call `op`, roll back on error. */
  const runOptimistic = (
    updater: (items: ActionItemView[]) => ActionItemView[],
    op: () => Promise<unknown>,
  ): void => {
    const prev = patchItems(updater);
    void (async () => {
      try {
        await op();
      } catch (err) {
        if (prev !== undefined && cacheKey !== null) qc.setQueryData(cacheKey, prev);
        // eslint-disable-next-line no-console
        console.warn('[editor] action item mutation failed', err);
      } finally {
        invalidate();
      }
    })();
  };

  const onToggleStatus = (a: ActionItemView): void => {
    if (noteId === null) return;
    const next: ActionItemView['status'] = a.status === 'resolved' ? 'open' : 'resolved';
    runOptimistic(
      (items) => items.map((it) => (it.actionItemId === a.actionItemId ? { ...it, status: next } : it)),
      () => setActionItemStatus({ actionItemId: a.actionItemId, status: next }),
    );
  };

  const beginEdit = (a: ActionItemView): void => {
    setEditingId(a.actionItemId);
    setDraftText(a.text);
    setDraftDue(toDateInput(a.dueDate));
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setDraftText('');
    setDraftDue('');
  };

  const saveEdit = (a: ActionItemView): void => {
    if (noteId === null) return;
    const text = draftText.trim();
    if (text.length === 0) {
      cancelEdit();
      return;
    }
    const hadDue = a.dueDate !== null;
    const clearDueDate = draftDue === '' && hadDue;
    // ISO date string for the optimistic cache (midnight); the server stores a date.
    const nextDue = draftDue === '' ? null : `${draftDue}T00:00:00`;
    runOptimistic(
      (items) =>
        items.map((it) =>
          it.actionItemId === a.actionItemId ? { ...it, text, dueDate: nextDue } : it,
        ),
      () =>
        updateActionItem({
          actionItemId: a.actionItemId,
          text,
          dueDate: draftDue === '' ? undefined : draftDue,
          clearDueDate,
        }),
    );
    cancelEdit();
  };

  const onDelete = (a: ActionItemView): void => {
    if (noteId === null) return;
    runOptimistic(
      (items) => items.filter((it) => it.actionItemId !== a.actionItemId),
      () => deleteActionItem({ actionItemId: a.actionItemId }),
    );
  };

  const submitNew = (): void => {
    if (noteId === null) return;
    const text = newText.trim();
    if (text.length === 0) return;
    const due = newDue === '' ? undefined : newDue;
    void (async () => {
      try {
        await createActionItem({ noteId, text, dueDate: due });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[editor] create action item failed', err);
      } finally {
        invalidate();
      }
    })();
    setNewText('');
    setNewDue('');
    setAdding(false);
  };

  const disabled = noteId === null;

  return (
    <div className="meta-sidecar__actions-wrap">
      {actionItems.length === 0 && !adding ? (
        <div className="meta-sidecar__empty">
          No action items yet — they'll appear as you write TODOs, or add one below.
        </div>
      ) : (
        <ul className="meta-sidecar__actions">
          {actionItems.map((a) => {
            const isEditing = editingId === a.actionItemId;
            const due = dueLabel(a.dueDate);
            return (
              <li key={a.actionItemId} className="meta-sidecar__action-row">
                <button
                  type="button"
                  className={'meta-sidecar__action-status meta-sidecar__action-status--' + a.status}
                  onClick={() => onToggleStatus(a)}
                  disabled={disabled || isEditing}
                  aria-pressed={a.status === 'resolved'}
                  title={a.status === 'resolved' ? 'Mark as open' : 'Mark as done'}
                  aria-label={
                    (a.status === 'resolved' ? 'Mark as open' : 'Mark as done') + `: ${a.text}`
                  }
                >
                  {a.status}
                </button>

                {isEditing ? (
                  <div className="meta-sidecar__action-edit">
                    <input
                      className="meta-sidecar__action-input"
                      value={draftText}
                      autoFocus
                      onChange={(e) => setDraftText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(a);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      aria-label="Action item text"
                    />
                    <div className="meta-sidecar__action-edit-row">
                      <input
                        type="date"
                        className="meta-sidecar__action-date"
                        value={draftDue}
                        onChange={(e) => setDraftDue(e.target.value)}
                        aria-label="Due date"
                      />
                      <button
                        type="button"
                        className="meta-sidecar__action-btn"
                        onClick={() => saveEdit(a)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="meta-sidecar__action-btn meta-sidecar__action-btn--ghost"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="meta-sidecar__action-text meta-sidecar__action-text--editable"
                    onClick={() => beginEdit(a)}
                    disabled={disabled}
                    title="Edit"
                  >
                    {a.text}
                    {due !== null && <span className="meta-sidecar__action-due">· due {due}</span>}
                    {a.origin === 'user' && (
                      <span className="meta-sidecar__action-origin" title="Added by you">
                        ·
                      </span>
                    )}
                  </button>
                )}

                {!isEditing && (
                  <button
                    type="button"
                    className="meta-sidecar__action-delete"
                    onClick={() => onDelete(a)}
                    disabled={disabled}
                    title="Delete action item"
                    aria-label={`Delete action item: ${a.text}`}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <div className="meta-sidecar__action-add">
          <input
            className="meta-sidecar__action-input"
            value={newText}
            autoFocus
            placeholder="New action item…"
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitNew();
              if (e.key === 'Escape') {
                setAdding(false);
                setNewText('');
                setNewDue('');
              }
            }}
            aria-label="New action item text"
          />
          <div className="meta-sidecar__action-edit-row">
            <input
              type="date"
              className="meta-sidecar__action-date"
              value={newDue}
              onChange={(e) => setNewDue(e.target.value)}
              aria-label="Due date"
            />
            <button
              type="button"
              className="meta-sidecar__action-btn"
              onClick={submitNew}
              disabled={newText.trim().length === 0}
            >
              Add
            </button>
            <button
              type="button"
              className="meta-sidecar__action-btn meta-sidecar__action-btn--ghost"
              onClick={() => {
                setAdding(false);
                setNewText('');
                setNewDue('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="meta-sidecar__action-add-toggle"
          onClick={() => setAdding(true)}
          disabled={disabled}
        >
          <Icon name="plus" size={12} />
          Add action item
        </button>
      )}
    </div>
  );
}
